import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, X, Loader2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLanes = () => {
  const { token } = useAuth();
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLane, setEditingLane] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    title_es: '',
    subtitle: '',
    subtitle_es: '',
    overline: '',
    overline_es: '',
    headline: '',
    headline_es: '',
    description: '',
    description_es: '',
    capabilities: '',
    capabilities_es: '',
    image_url: '',
    display_order: 0,
    active: true
  });

  useEffect(() => {
    fetchLanes();
  }, []);

  const fetchLanes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/lanes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLanes(data);
      }
    } catch (error) {
      console.error('Error fetching lanes:', error);
      toast.error('Failed to load lanes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingLane(null);
    setFormData({
      title: '',
      title_es: '',
      subtitle: '',
      subtitle_es: '',
      overline: '',
      overline_es: '',
      headline: '',
      headline_es: '',
      description: '',
      description_es: '',
      capabilities: '',
      capabilities_es: '',
      image_url: '',
      display_order: lanes.length,
      active: true
    });
  };

  const handleEdit = (lane) => {
    setEditingLane(lane);
    setFormData({
      title: lane.title || '',
      title_es: lane.title_es || '',
      subtitle: lane.subtitle || '',
      subtitle_es: lane.subtitle_es || '',
      overline: lane.overline || '',
      overline_es: lane.overline_es || '',
      headline: lane.headline || '',
      headline_es: lane.headline_es || '',
      description: lane.description || '',
      description_es: lane.description_es || '',
      capabilities: (lane.capabilities || []).join('\n'),
      capabilities_es: (lane.capabilities_es || []).join('\n'),
      image_url: lane.image_url || '',
      display_order: lane.display_order || 0,
      active: lane.active !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        capabilities: formData.capabilities.split('\n').map(s => s.trim()).filter(Boolean),
        capabilities_es: formData.capabilities_es ? formData.capabilities_es.split('\n').map(s => s.trim()).filter(Boolean) : [],
        display_order: parseInt(formData.display_order) || 0
      };

      const url = editingLane 
        ? `${API_URL}/api/admin/lanes/${editingLane.id}`
        : `${API_URL}/api/admin/lanes`;
      
      const method = editingLane ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(editingLane ? 'Lane updated!' : 'Lane created!');
        setShowModal(false);
        resetForm();
        fetchLanes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save lane');
      }
    } catch (error) {
      console.error('Error saving lane:', error);
      toast.error('Failed to save lane');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lane?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/lanes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Lane deleted!');
        fetchLanes();
      } else {
        toast.error('Failed to delete lane');
      }
    } catch (error) {
      console.error('Error deleting lane:', error);
      toast.error('Failed to delete lane');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-terracota" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-bold text-stone-900">Three Lanes</h1>
          <p className="text-stone-500 mt-1">Manage the three main service lanes on your homepage</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-terracota text-white px-4 py-2 rounded-xl hover:bg-terracota/90 transition-colors"
          data-testid="add-lane-btn"
        >
          <Plus className="w-5 h-5" />
          Add Lane
        </button>
      </div>

      {/* Lanes List */}
      <div className="space-y-4">
        {lanes.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <GripVertical className="w-8 h-8 text-stone-400" />
            </div>
            <h3 className="text-lg font-medium text-stone-900 mb-2">No lanes yet</h3>
            <p className="text-stone-500 mb-4">Add your three main service lanes to display on the homepage</p>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="text-terracota hover:underline font-medium"
            >
              Add your first lane
            </button>
          </div>
        ) : (
          lanes.map((lane, index) => (
            <div
              key={lane.id}
              className="bg-white rounded-2xl p-6 border border-stone-200 hover:border-terracota/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-terracota/10 rounded-xl text-terracota font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="text-xs text-terracota font-semibold tracking-wider uppercase mb-1">
                      {lane.overline || 'LANE'}
                    </div>
                    <h3 className="text-xl font-serif font-bold text-stone-900">{lane.title}</h3>
                    {lane.title_es && (
                      <p className="text-sm text-stone-400 mt-0.5">ES: {lane.title_es}</p>
                    )}
                    <p className="text-stone-600 mt-2 max-w-2xl">{lane.description}</p>
                    {lane.capabilities && lane.capabilities.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {lane.capabilities.slice(0, 3).map((cap, i) => (
                          <span key={i} className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-full">
                            {cap}
                          </span>
                        ))}
                        {lane.capabilities.length > 3 && (
                          <span className="text-xs text-stone-400">+{lane.capabilities.length - 3} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${lane.active ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                    {lane.active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => handleEdit(lane)}
                    className="p-2 rounded-lg hover:bg-stone-100 transition-colors"
                    data-testid={`edit-lane-${lane.id}`}
                  >
                    <Edit className="w-5 h-5 text-stone-500" />
                  </button>
                  <button
                    onClick={() => handleDelete(lane.id)}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                    data-testid={`delete-lane-${lane.id}`}
                  >
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-stone-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-serif font-bold text-stone-900">
                {editingLane ? 'Edit Lane' : 'Add Lane'}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 rounded-lg hover:bg-stone-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Title (EN) *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Brand Presence"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Title (ES)</label>
                  <input
                    type="text"
                    value={formData.title_es}
                    onChange={(e) => setFormData({ ...formData, title_es: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Presencia de Marca"
                  />
                </div>
              </div>

              {/* Overline Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Overline (EN)</label>
                  <input
                    type="text"
                    value={formData.overline}
                    onChange={(e) => setFormData({ ...formData, overline: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="DIGITAL STRATEGY"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Overline (ES)</label>
                  <input
                    type="text"
                    value={formData.overline_es}
                    onChange={(e) => setFormData({ ...formData, overline_es: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="ESTRATEGIA DIGITAL"
                  />
                </div>
              </div>

              {/* Subtitle Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Subtitle (EN)</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Digital Strategy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Subtitle (ES)</label>
                  <input
                    type="text"
                    value={formData.subtitle_es}
                    onChange={(e) => setFormData({ ...formData, subtitle_es: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Estrategia Digital"
                  />
                </div>
              </div>

              {/* Headline Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Headline (EN) *</label>
                  <input
                    type="text"
                    value={formData.headline}
                    onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Launch & Campaign Strategy"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Headline (ES)</label>
                  <input
                    type="text"
                    value={formData.headline_es}
                    onChange={(e) => setFormData({ ...formData, headline_es: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Estrategia de Lanzamiento"
                  />
                </div>
              </div>

              {/* Description Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Description (EN) *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 h-24"
                    placeholder="Brand moments designed to live beyond the room..."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Description (ES)</label>
                  <textarea
                    value={formData.description_es}
                    onChange={(e) => setFormData({ ...formData, description_es: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 h-24"
                    placeholder="Momentos de marca diseñados para vivir más allá..."
                  />
                </div>
              </div>

              {/* Capabilities Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Capabilities (EN)</label>
                  <textarea
                    value={formData.capabilities}
                    onChange={(e) => setFormData({ ...formData, capabilities: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 h-32"
                    placeholder="Launch Strategy + Rollout Plan&#10;Campaign Concepts&#10;Content Architecture"
                  />
                  <p className="text-xs text-stone-400 mt-1">One capability per line</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Capabilities (ES)</label>
                  <textarea
                    value={formData.capabilities_es}
                    onChange={(e) => setFormData({ ...formData, capabilities_es: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50 h-32"
                    placeholder="Estrategia de Lanzamiento&#10;Conceptos de Campaña&#10;Arquitectura de Contenido"
                  />
                  <p className="text-xs text-stone-400 mt-1">Una capacidad por línea</p>
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  placeholder="https://..."
                />
              </div>

              {/* Display Order & Active */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    min="0"
                  />
                </div>
                <div className="flex items-center gap-3 pt-8">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-5 h-5 rounded border-stone-300 text-terracota focus:ring-terracota"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-stone-700">Active</label>
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-6 py-3 border border-stone-300 rounded-xl hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-terracota text-white rounded-xl hover:bg-terracota/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingLane ? 'Update Lane' : 'Create Lane'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLanes;
